import { DashboardFlowTransitionAPI } from '@/API/DashboardFlowTransition'
import type { FlowTransitionState } from '@/API/DashboardFlowTransition'
import type { AppDispatch, RootState } from '@/redux'
import { chatSessionActions } from '@/redux/slice/chatSessionSlice'
import { dashboardCreationActions } from '@/redux/slice/dashboardCreationSlice'
import type { ChatMessage } from '@/types/Dashboards.types'

/**
 * DashboardFlowTransitionService — state-sync thunk for FSM gate transitions.
 *
 * `flowTransitionThunk` fires `POST /v1/dashboards/:id/flow-transition` at
 * each of the four deterministic gate moments (source pick, credential pick,
 * domain pick, filter confirm). On success it appends both template messages
 * (user + assistant) to the chat thread via `chatSessionActions.appendMessage`
 * so the chat panel shows the conversation history without a page refresh.
 *
 * Returns a `FlowTransitionResult` so callers can gate downstream work
 * (notably `proposePanelsThunk`) on whether the transition actually landed.
 *
 * Bug #3 from 2026-05-14 bugbash — the thunk previously returned `void` and
 * swallowed both 409 and generic failures with a `console.warn`. Callers
 * unconditionally fired `proposePanelsThunk` afterwards. When the transition
 * failed (e.g. mid rate-limit pressure), the proposed-panels endpoint then
 * 409'd against the stale FSM state and the user saw a misleading
 * "Please re-confirm your filters / sequencing issue" message. The fix is to
 * surface the result so callers can branch.
 *
 * The eager-append of both template messages is preserved — that UX is
 * unaffected by network failure (the BE persists nothing on transition
 * failure, so a refresh would simply not show them).
 *
 * **FSM drift fix (2026-05-28):** the thunk now captures the prior
 * `flowState` BEFORE the optimistic dispatch and REVERTS it on HTTP failure.
 * This eliminates a class of FE↔BE drift where the FE optimistically
 * advanced Redux but the HTTP request never landed (network blip, race with
 * `dashboardId` propagation during bootstrap, 5xx, etc.). Pre-fix, the next
 * gate click would fire against a stale BE state and produce
 * `409 INVALID_FSM_TRANSITION`. Post-fix, on any non-success the Redux
 * `flowState` returns to its prior value and the user can re-click the
 * current gate to retry. Surfaced during YoY manual-test 2026-05-28; routing
 * memo: `cto1-route-dashboard-creation-fsm-drift.md`.
 *
 * Idempotency: the caller provides a pre-generated key per click-invocation.
 * The backend de-duplicates within a 60s TTL window.
 *
 * Spec: plans/2026-04-29-step-5-deterministic-panels-and-chat-context.md §Slice 1.3
 */
export interface FlowTransitionThunkArgs {
  toState: FlowTransitionState
  contextDelta: Record<string, unknown>
  userBubble: string
  assistantBubble: string
}

/**
 * Result of a `flowTransitionThunk` invocation. Callers that gate downstream
 * work (e.g. `proposePanelsThunk`) on a successful FSM advance check `ok`.
 * Callers that intentionally fire-and-forget can ignore the return value.
 */
export interface FlowTransitionResult {
  ok: boolean
  status?: number
  errorCode?: string
}

export const flowTransitionThunk =
  (token: string, dashboardId: string, args: FlowTransitionThunkArgs, idempotencyKey?: string) =>
  async (dispatch: AppDispatch, getState: () => RootState): Promise<FlowTransitionResult> => {
    // Capture the prior flowState BEFORE the optimistic dispatch so we can
    // revert on HTTP failure. This is the FSM-drift-prevention contract — see
    // file header doc-block "FSM drift fix (2026-05-28)" for rationale.
    const priorFlowState = getState().dashboardCreation.flowState

    // Eagerly append both template messages to the chat thread so the user
    // sees them immediately without waiting for the network round-trip.
    //
    // Timestamps: assistant is stamped +1ms after user so any downstream
    // sort/merge (e.g. mergeMessagesByTimestamp) produces a deterministic
    // user-then-assistant order regardless of the JS engine's sort stability.
    const nowMs = Date.now()
    const userTs = new Date(nowMs).toISOString()
    const assistantTs = new Date(nowMs + 1).toISOString()

    const userMsg: ChatMessage = {
      role: 'user',
      type: 'text',
      kind: 'template',
      content: args.userBubble,
      timestamp: userTs,
    }
    const assistantMsg: ChatMessage = {
      role: 'assistant',
      type: 'text',
      kind: 'template',
      content: args.assistantBubble,
      timestamp: assistantTs,
    }

    dispatch(chatSessionActions.appendMessage(userMsg))
    dispatch(chatSessionActions.appendMessage(assistantMsg))

    // Keep FE Redux flow state in sync with the transition being recorded.
    // This is a belt-and-suspenders guard: the gate components already dispatch
    // setFlowState before calling this thunk, but we sync here to be explicit.
    dispatch(dashboardCreationActions.setFlowState(args.toState))

    // Fire the state-sync endpoint. Result is awaited and reflected back to
    // the caller; on failure we revert flowState to its captured prior value.
    const result = await DashboardFlowTransitionAPI.transition(token, {
      dashboardId,
      toState: args.toState,
      contextDelta: args.contextDelta,
      userBubble: args.userBubble,
      assistantBubble: args.assistantBubble,
      idempotencyKey,
    })

    if (result.err !== undefined || !result.data) {
      // Revert the optimistic flowState update so FE↔BE remain in sync. The
      // template messages stay (per file header — UX trade-off; on refresh
      // they disappear because BE persisted nothing).
      dispatch(dashboardCreationActions.setFlowState(priorFlowState))

      if (result.status === 409) {
        // FSM violation — BE rejected the transition because flow_state on
        // the session disagrees with what the FE thinks it is.
        console.warn(
          '[DashboardFlowTransitionService] FSM transition rejected (409). Reverted flowState.',
          'toState:', args.toState,
          'revertedTo:', priorFlowState,
          'errorCode:', result.errorCode
        )
      } else {
        console.warn(
          '[DashboardFlowTransitionService] Error syncing flow state. Reverted flowState.',
          'toState:', args.toState,
          'revertedTo:', priorFlowState,
          'status:', result.status,
          'errorCode:', result.errorCode
        )
      }
      return {
        ok: false,
        status: result.status,
        errorCode: result.errorCode,
      }
    }

    // On success the server echoes back `templateMessages` as persisted rows.
    // The messages were already appended eagerly above (with a local timestamp).
    // We do NOT re-append from the server response — that would produce
    // duplicates. The eager append is the authoritative FE view; the server
    // response is the persistence confirmation.
    return { ok: true }
  }
